/**
 * Votações — morador (Portal do Condômino) — 0.1.48.
 *
 * O morador vê as votações abertas da sua unidade e vota (uma vez por unidade),
 * recebendo um comprovante (protocolo). Nas encerradas, vê o resultado e a ata.
 * 0.1.88: ENQUETE — quem mora responde (1 por apto), sem ata; o resultado aparece ao encerrar.
 * 0.1.88: ASSEMBLEIAS convocadas no topo (edital e modelo de procuração em PDF).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, CheckCircle2, FileText, Loader2, ShieldCheck, Vote,
} from "lucide-react";
import { useState } from "react";
import { api, ApiError, ataVotacaoUrl } from "../lib/api";
import { AssembleiasMorador } from "./AssembleiasPainel";

interface Votacao {
  id: number; titulo: string; status: string; sigilo: string; peso: string;
  fecha_em: string | null; aberta_agora: boolean; ja_votei?: boolean; modalidade?: string;
  minhas_unidades?: string[];
  /** 0.1.83: aptos da pessoa em que ela não vota (inquilino/morador sem procuração). */
  unidades_sem_voto?: string[];
}
interface PautaD { id: number; pergunta: string; opcoes: string[] }
interface Detalhe extends Votacao {
  descricao: string | null;
  pautas: PautaD[];
  meu_voto: Record<number, { opcao: string; protocolo: string; unidade: string }>;
  resultado?: { quorum: { unidades_votantes: number; unidades_total: number; percentual: number };
    pautas: { pergunta: string; vencedora: string | null; empate: boolean;
      por_opcao: { opcao: string; votos: number; percentual: number }[] }[] };
}

export function VotacoesMoradorPage({ condominioId, onVoltar }: { condominioId: number; onVoltar: () => void }) {
  const qc = useQueryClient();
  const [abertaId, setAbertaId] = useState<number | null>(null);

  const { data: lista, isLoading } = useQuery<Votacao[]>({
    queryKey: ["minhas-votacoes", condominioId],
    queryFn: () => api.get(`/condominios/${condominioId}/votacoes/minhas`),
  });

  return (
    <div className="max-w-lg mx-auto px-3 py-4">
      <div className="flex items-center gap-2 mb-4">
        <button onClick={onVoltar} className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1.5 rounded-md border text-slate-600 bg-white border-slate-200 hover:bg-slate-50">
          <ArrowLeft size={16} /> Voltar
        </button>
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <Vote size={20} className="text-brand-700" /> Assembleias e votações
        </h1>
      </div>

      <AssembleiasMorador condominioId={condominioId} />

      {isLoading && <p className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Carregando…</p>}
      {lista && lista.length === 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600">
          Nenhuma votação no momento. Quando o síndico abrir uma, ela aparece aqui.
        </div>
      )}

      <div className="space-y-3">
        {lista?.map((v) => (
          <div key={v.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">
                  {v.modalidade === "ENQUETE" && <span className="text-violet-700 font-semibold">Enquete · </span>}
                  {v.status === "ABERTA" ? (v.aberta_agora ? "Aberta" : "Fora do prazo") : "Encerrada"}
                  {v.modalidade !== "ENQUETE" && <>{" · "}{v.sigilo === "SECRETO" ? "voto secreto" : "voto aberto"}</>}
                </div>
                <div className="text-base font-bold text-slate-900">{v.titulo}</div>
              </div>
              {v.ja_votei && v.status === "ABERTA" && (
                <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-100 text-green-700 flex-shrink-0">{v.modalidade === "ENQUETE" ? "Você respondeu" : "Você votou"}</span>
              )}
            </div>

            <div className="mt-3">
              {abertaId === v.id ? (
                <Detalhe vid={v.id} onVotou={() => { qc.invalidateQueries({ queryKey: ["minhas-votacoes", condominioId] }); }} />
              ) : (
                <button onClick={() => setAbertaId(v.id)}
                  className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-brand-700 text-white">
                  {v.status === "ABERTA"
                    ? (v.modalidade === "ENQUETE" ? (v.ja_votei ? "Ver / alterar minha resposta" : "Responder")
                      : (v.ja_votei ? "Ver / alterar meu voto" : "Votar"))
                    : "Ver resultado"}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Detalhe({ vid, onVotou }: { vid: number; onVotou: () => void }) {
  const qc = useQueryClient();
  const [escolhas, setEscolhas] = useState<Record<number, string>>({});
  const [protocolo, setProtocolo] = useState<string | null>(null);

  const { data: d, isLoading } = useQuery<Detalhe>({
    queryKey: ["votacao", vid],
    queryFn: () => api.get(`/votacoes/${vid}`),
  });

  const votar = useMutation({
    mutationFn: () =>
      api.post<{ protocolo: string }>(`/votacoes/${vid}/votar`, {
        votos: Object.entries(escolhas).map(([pauta_id, opcao]) => ({ pauta_id: Number(pauta_id), opcao })),
      }),
    onSuccess: (r) => {
      setProtocolo(r.protocolo);
      qc.invalidateQueries({ queryKey: ["votacao", vid] });
      onVotou();
    },
  });

  if (isLoading || !d) return <p className="text-sm text-slate-500">Carregando…</p>;

  const encerrada = d.status === "ENCERRADA";
  const enquete = d.modalidade === "ENQUETE";
  const podeVotar = d.status === "ABERTA" && d.aberta_agora && (d.minhas_unidades?.length ?? 0) > 0;
  const escolhaDe = (pid: number) => escolhas[pid] ?? d.meu_voto?.[pid]?.opcao ?? "";

  return (
    <div className="mt-1">
      {d.descricao && <p className="text-sm text-slate-600 mb-2 whitespace-pre-wrap">{d.descricao}</p>}

      {/* Resultado (encerrada) */}
      {encerrada && d.resultado && (
        <div className="space-y-3">
          <div className="text-xs text-slate-500">
            Quórum: {d.resultado.quorum.unidades_votantes}/{d.resultado.quorum.unidades_total} unidades ({d.resultado.quorum.percentual}%)
          </div>
          {d.resultado.pautas.map((p, i) => (
            <div key={i} className="rounded-md border border-slate-200 p-2">
              <div className="text-sm font-medium text-slate-800">{p.pergunta}</div>
              {p.por_opcao.map((o) => (
                <div key={o.opcao} className="flex justify-between text-sm text-slate-600">
                  <span>{o.opcao}</span><span>{o.votos} ({o.percentual}%)</span>
                </div>
              ))}
              {p.empate ? <div className="text-xs text-amber-700 font-medium mt-1">Empate</div>
                : p.vencedora && <div className="text-xs text-green-700 font-medium mt-1">{enquete ? "Mais escolhida" : "Aprovado"}: {p.vencedora}</div>}
            </div>
          ))}
          {!enquete && (
            <a href={ataVotacaoUrl(vid)} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-slate-300 text-slate-700">
              <FileText size={14} /> Baixar ata (PDF)
            </a>
          )}
        </div>
      )}

      {/* Votação (aberta) */}
      {!encerrada && d.pautas.map((p) => (
        <div key={p.id} className="rounded-md border border-slate-200 p-2 mb-2">
          <div className="text-sm font-medium text-slate-800 mb-1">{p.pergunta}</div>
          <div className="space-y-1">
            {p.opcoes.map((o) => (
              <label key={o} className="flex items-center gap-2 text-sm text-slate-700">
                <input type="radio" name={`p${p.id}`} value={o} checked={escolhaDe(p.id) === o}
                  disabled={!podeVotar}
                  onChange={() => setEscolhas({ ...escolhas, [p.id]: o })} />
                {o}
              </label>
            ))}
          </div>
        </div>
      ))}

      {!encerrada && (
        <>
          {enquete && (d.minhas_unidades?.length ?? 0) === 0 && (
            <p className="text-xs text-amber-700 mb-2">A enquete é respondida por quem mora no apartamento.</p>
          )}
          {!enquete && (d.minhas_unidades?.length ?? 0) === 0 && (d.unidades_sem_voto?.length ?? 0) > 0 && (
            <p className="text-xs text-amber-700 mb-2">
              Nas assembleias quem vota é o <b>proprietário</b>. Para você votar no lugar dele, o
              proprietário precisa assinar uma <b>procuração</b> e entregá-la ao síndico.
            </p>
          )}
          {!enquete && (d.minhas_unidades?.length ?? 0) === 0 && (d.unidades_sem_voto?.length ?? 0) === 0 && (
            <p className="text-xs text-amber-700 mb-2">Seu e-mail não está ligado a uma unidade. Fale com o síndico.</p>
          )}
          {protocolo ? (
            <div className="rounded-md bg-green-50 border border-green-200 p-2 text-sm text-green-800 flex items-center gap-2">
              <ShieldCheck size={16} /> {enquete ? "Resposta registrada!" : "Voto registrado!"} Comprovante: <b>{protocolo}</b>
            </div>
          ) : (
            <button disabled={!podeVotar || votar.isPending || Object.keys(escolhas).length === 0}
              onClick={() => votar.mutate()}
              className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-md bg-brand-700 text-white disabled:opacity-50">
              {votar.isPending ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
              {enquete
                ? (d.meu_voto && Object.keys(d.meu_voto).length ? "Alterar minha resposta" : "Responder")
                : (d.meu_voto && Object.keys(d.meu_voto).length ? "Alterar meu voto" : "Confirmar voto")}
            </button>
          )}
          {votar.isError && (
            <p className="text-xs text-rose-600 mt-1">
              {votar.error instanceof ApiError ? votar.error.message
                : "Não foi possível votar (fora do prazo, unidade inadimplente ou sem unidade)."}
            </p>
          )}
        </>
      )}
    </div>
  );
}
